/**
 * Shopify cart drawer — bal-com-v4 aside--cart layout, backed by /cart.js API.
 */
(function () {
  "use strict";

  const FREE_SHIP_THRESHOLD = 60;
  const cartRoot = document.querySelector("[data-cart-root]");
  const emptyEl = document.querySelector("[data-cart-empty]");
  const filledEl = document.querySelector("[data-cart-filled]");
  const footerEl = document.querySelector("[data-cart-footer]");
  const countEl = document.querySelector(".header__cart-count");
  const cartBtn = document.querySelector(".header__cart-btn");
  const cartOverlay = document.getElementById("overlay-cart");

  if (!cartRoot || !emptyEl || !filledEl) return;

  // Keep promo panel open across cart re-renders (footer is rebuilt after each refresh).
  let discountPanelOpen = false;
  let discountStatusMessage = "";
  let discountStatusIsError = false;

  function cartRootUrl(path) {
    const base = window.Shopify?.routes?.root || "/";
    return `${base}${path.replace(/^\//, "")}`;
  }

  function esc(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function fmtMoney(cents) {
    const n = Number(cents) / 100;
    return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
  }

  const productHeroCache = {};

  function heroUrlForHandle(handle) {
    if (!handle) return "";
    if (window.FB_PDP_HERO_URLS && window.FB_PDP_HERO_URLS[handle]) {
      return window.FB_PDP_HERO_URLS[handle];
    }
    if (window.FB_CROSS_SELL_IMAGES && window.FB_CROSS_SELL_IMAGES[handle]) {
      return window.FB_CROSS_SELL_IMAGES[handle];
    }
    if (Object.prototype.hasOwnProperty.call(productHeroCache, handle)) {
      return productHeroCache[handle];
    }
    return "";
  }

  async function prefetchProductHeroUrls(handles) {
    const missing = handles.filter(function (handle) {
      return handle && !heroUrlForHandle(handle) && productHeroCache[handle] === undefined;
    });
    if (!missing.length) return;

    await Promise.all(
      missing.map(async function (handle) {
        try {
          const res = await fetch(cartRootUrl("/products/" + encodeURIComponent(handle) + ".js"), {
            headers: { Accept: "application/json" },
          });
          if (!res.ok) {
            productHeroCache[handle] = "";
            return;
          }
          const product = await res.json();
          const raw = product.featured_image || (product.images && product.images[0]) || "";
          productHeroCache[handle] = normalizeImageUrl(raw);
        } catch (_err) {
          productHeroCache[handle] = "";
        }
      }),
    );
  }

  function normalizeImageUrl(url) {
    if (!url) return "";
    if (url.indexOf("//") === 0) return "https:" + url;
    return url;
  }

  function sizedImageUrl(rawUrl) {
    const baseUrl = normalizeImageUrl(rawUrl);
    if (!baseUrl) return { src: "", srcset: "" };
    const join = baseUrl.indexOf("?") >= 0 ? "&" : "?";
    return {
      src: baseUrl + join + "width=96",
      srcset: baseUrl + join + "width=96 96w, " + baseUrl + join + "width=192 192w",
    };
  }

  function lineImageUrl(line) {
    const handle = line.handle || "";
    // Match PDP + homepage cards: theme hero first, then Shopify featured image, then cart line image.
    let raw = heroUrlForHandle(handle) || "";
    if (!raw && line.featured_image) {
      raw = typeof line.featured_image === "string" ? line.featured_image : line.featured_image.url || "";
    }
    if (!raw) raw = line.image || "";
    return sizedImageUrl(raw);
  }

  function isSubscribeSaveLine(line) {
    return Boolean(line.selling_plan_allocation?.selling_plan);
  }

  function cartHasSubscribeSave(items) {
    return items.some(isSubscribeSaveLine);
  }

  function purchaseLabel(line) {
    // Any line with a subscription plan attached is a Subscribe & Save purchase.
    // We show a consistent label to match the product page toggle wording,
    // rather than the raw plan name the subscription app generates.
    if (isSubscribeSaveLine(line)) {
      return "Subscribe & Save";
    }
    if (line.variant_title && line.variant_title !== "Default Title") {
      return line.variant_title;
    }
    return "One Time Purchase";
  }

  function openDrawer() {
    if (!cartOverlay) return;
    cartOverlay.classList.add("expanded");
    document.documentElement.style.overflow = "hidden";
  }

  async function fetchCart() {
    const res = await fetch(cartRootUrl("/cart.js"), { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("Could not load cart");
    return res.json();
  }

  function renderDiscountPanel() {
    const applied = discountStatusMessage
      ? `<p class="cart-discount-form__status${discountStatusIsError ? " cart-discount-form__status--error" : " cart-discount-form__status--success"}" data-cart-discount-status role="status" aria-live="polite">${esc(discountStatusMessage)}</p>`
      : `<p class="cart-discount-form__status" data-cart-discount-status role="status" aria-live="polite"></p>`;

    return (
      `<div class="cart-discount-toggle__panel"${discountPanelOpen ? "" : " hidden"}>` +
      `<form class="cart-discount-form" data-cart-discount-form novalidate>` +
      `<label class="sr-only" for="cart-discount-input">Discount or gift card code</label>` +
      `<div class="cart-discount-form__row">` +
      `<input type="text" id="cart-discount-input" class="cart-discount-form__input" name="discount" placeholder="Promo or gift card code" autocomplete="off" data-cart-discount-input />` +
      `<button type="submit" class="cart-discount-form__apply" data-cart-discount-apply>Apply</button>` +
      `</div>` +
      applied +
      `</form></div>`
    );
  }

  function syncDiscountPanelUi() {
    if (!footerEl) return;
    const btn = footerEl.querySelector("[data-cart-discount-toggle]");
    const panel = footerEl.querySelector(".cart-discount-toggle__panel");
    if (btn) btn.setAttribute("aria-expanded", discountPanelOpen ? "true" : "false");
    if (panel) panel.hidden = !discountPanelOpen;
  }

  async function applyDiscountCode(code) {
    const trimmed = String(code || "").trim();
    if (!trimmed) {
      discountStatusMessage = "Enter a promo or gift card code.";
      discountStatusIsError = true;
      discountPanelOpen = true;
      syncDiscountPanelUi();
      updateDiscountStatusEl();
      return;
    }

    discountStatusMessage = "Applying…";
    discountStatusIsError = false;
    discountPanelOpen = true;
    syncDiscountPanelUi();
    updateDiscountStatusEl();

    try {
      const returnPath = window.location.pathname + window.location.search;
      const url =
        cartRootUrl("/discount/" + encodeURIComponent(trimmed)) +
        "?redirect=" +
        encodeURIComponent(returnPath);

      const res = await fetch(url, { credentials: "same-origin", redirect: "manual" });

      if (res.status === 404) {
        discountStatusMessage = "That code didn't work. Check it and try again.";
        discountStatusIsError = true;
        updateDiscountStatusEl();
        return;
      }

      discountStatusMessage = "Code applied — your discount will show at checkout.";
      discountStatusIsError = false;
      await refresh();
    } catch (_err) {
      discountStatusMessage = "Couldn't apply that code. Please try again.";
      discountStatusIsError = true;
      updateDiscountStatusEl();
    }
  }

  function updateDiscountStatusEl() {
    const statusEl = footerEl?.querySelector("[data-cart-discount-status]");
    if (!statusEl) return;
    statusEl.textContent = discountStatusMessage;
    statusEl.classList.toggle("cart-discount-form__status--error", discountStatusIsError);
    statusEl.classList.toggle("cart-discount-form__status--success", Boolean(discountStatusMessage) && !discountStatusIsError);
  }

  function renderLine(line) {
    const img = lineImageUrl(line);
    const alt = esc(line.product_title);
    const imgTag = img.src
      ? `<img src="${esc(img.src)}" srcset="${esc(img.srcset)}" sizes="96px" alt="${alt}" width="96" height="96" decoding="async" loading="lazy" class="cart-line__img" style="aspect-ratio:1/1" />`
      : `<span class="cart-line__img cart-line__img--fallback" aria-hidden="true"></span>`;

    return (
      `<li class="cart-line" data-line-key="${esc(line.key)}">` +
      `<a href="${esc(line.url)}" class="cart-line__img-link" data-overlay-close="overlay-cart">` +
      imgTag +
      `</a>` +
      `<div class="cart-line__info">` +
      `<p class="cart-line__header"><a href="${esc(line.url)}" data-overlay-close="overlay-cart">${esc(line.product_title)}</a></p>` +
      `<ul><li>${esc(purchaseLabel(line))}</li></ul>` +
      `<div class="cart-line__item-update">` +
      `<div class="cart-line__qty-pill cart-line__quantity" role="group" aria-label="Quantity">` +
      `<button type="button" class="cart-line__qty-btn" data-cart-dec="${esc(line.key)}" aria-label="${line.quantity <= 1 ? "Remove from cart" : "Decrease quantity"}"><span>− </span></button>` +
      `<p class="cart-line__qty-num" aria-live="polite">${line.quantity}</p>` +
      `<button type="button" class="cart-line__qty-btn" data-cart-inc="${esc(line.key)}" aria-label="Increase quantity"><span>+</span></button>` +
      `</div>` +
      `<div class="cart-line__price"><span><div>${fmtMoney(line.final_line_price)}</div></span></div>` +
      `</div></div></li>`
    );
  }

  function render(cart) {
    const items = cart.items || [];
    const hasItems = items.length > 0;
    const qty = items.reduce(function (sum, line) {
      return sum + line.quantity;
    }, 0);
    const total = Number(cart.total_price || 0) / 100;
    const hasSubscribeSave = cartHasSubscribeSave(items);
    const remaining = Math.max(0, FREE_SHIP_THRESHOLD - total);
    const progressPct = hasSubscribeSave
      ? 100
      : Math.min(100, (total / FREE_SHIP_THRESHOLD) * 100);

    emptyEl.hidden = hasItems;
    filledEl.hidden = !hasItems;
    if (footerEl) footerEl.hidden = !hasItems;

    if (countEl) {
      countEl.textContent = String(qty);
      countEl.dataset.empty = qty === 0 ? "true" : "false";
    }
    if (cartBtn) {
      cartBtn.setAttribute("aria-label", qty === 1 ? "Cart, 1 item" : "Cart, " + qty + " items");
    }

    if (!hasItems) {
      filledEl.innerHTML = "";
      if (footerEl) footerEl.innerHTML = "";
      return;
    }

    let freeshipText;
    let freeshipClass = "cart-freeship";
    if (hasSubscribeSave) {
      freeshipClass += " cart-freeship--subscribe";
      freeshipText =
        '<span class="cart-freeship__accent">Subscribe &amp; Save</span> includes ' +
        '<span class="cart-freeship__accent">free shipping</span> on this order and every delivery ✿';
    } else if (remaining > 0) {
      freeshipText =
        '<span class="cart-freeship__accent">Free shipping</span> is only <span class="cart-freeship__accent">' +
        fmtMoney(remaining * 100) +
        "</span> away";
    } else {
      freeshipClass += " cart-freeship--unlocked";
      freeshipText = '<span class="cart-freeship__accent">You unlocked free shipping ✿</span>';
    }

    filledEl.innerHTML =
      `<div class="${freeshipClass}">` +
      `<p class="cart-freeship__text">${freeshipText}</p>` +
      `<div class="cart-freeship__bar" role="progressbar" aria-valuenow="${Math.round(progressPct)}" aria-valuemin="0" aria-valuemax="100">` +
      `<div class="cart-freeship__bar-fill" style="width:${progressPct}%"></div>` +
      `</div></div>` +
      `<div class="cart-details">` +
      `<p class="cart-main__header">Your Cart:</p>` +
      `<p id="cart-lines" class="sr-only">Line items</p>` +
      `<div><ul aria-labelledby="cart-lines">` +
      items.map(renderLine).join("") +
      `</ul></div></div>`;

    const checkoutUrl = cart.checkout_url || cartRootUrl("/checkout");

    if (footerEl) {
      footerEl.innerHTML =
        `<div class="cart-checkout-actions">` +
        `<p class="cart-checkout-actions__heading">Order Summary</p>` +
        `<div class="cart-discount-toggle">` +
        `<button type="button" class="cart-discount-toggle__btn" aria-expanded="${discountPanelOpen ? "true" : "false"}" data-cart-discount-toggle>` +
        `<span>Have a discount or gift card?</span><span aria-hidden="true">+</span>` +
        `</button>` +
        renderDiscountPanel() +
        `</div>` +
        `<div class="cart-checkout-actions__main">` +
        `<div class="cart-checkout-actions__subtotal">` +
        `<span class="cart-checkout-actions__subtotal-label">Subtotal</span>` +
        `<span class="cart-checkout-actions__subtotal-amount">${fmtMoney(cart.total_price)}</span>` +
        `</div>` +
        `<p class="cart-checkout-actions__tax">Tax at checkout</p>` +
        `<p class="cart-checkout-actions__guarantee">90 day money-back guarantee</p>` +
        `</div>` +
        `<a class="cart-checkout-cta" href="${esc(checkoutUrl)}">Proceed to Checkout</a>` +
        `</div>`;
    }
  }

  async function renderCart(cart) {
    const handles = (cart.items || [])
      .map(function (line) {
        return line.handle;
      })
      .filter(Boolean);
    await prefetchProductHeroUrls(handles);
    render(cart);
  }

  async function refresh() {
    try {
      renderCart(await fetchCart());
    } catch (err) {
      console.error(err);
    }
  }

  async function changeLine(key, quantity) {
    const res = await fetch(cartRootUrl("/cart/change.js"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
    if (!res.ok) throw new Error("Could not update cart");
    renderCart(await res.json());
  }

  cartRoot.addEventListener("click", function (e) {
    const target = e.target.closest("[data-cart-inc], [data-cart-dec]");
    if (!target) return;
    e.preventDefault();
    const key = target.getAttribute("data-cart-inc") || target.getAttribute("data-cart-dec");
    if (!key) return;

    const row = target.closest(".cart-line");
    const qtyEl = row?.querySelector(".cart-line__qty-num");
    const current = Number(qtyEl?.textContent || 1);
    const nextQty = target.hasAttribute("data-cart-inc") ? current + 1 : current - 1;
    changeLine(key, Math.max(0, nextQty));
  });

  // Footer (discount toggle + promo form) sits outside [data-cart-root] — listen on the drawer overlay.
  cartOverlay?.addEventListener("click", function (e) {
    const discountBtn = e.target.closest("[data-cart-discount-toggle]");
    if (discountBtn) {
      discountPanelOpen = !discountPanelOpen;
      syncDiscountPanelUi();
      if (discountPanelOpen) {
        footerEl?.querySelector("[data-cart-discount-input]")?.focus();
      }
      return;
    }

    if (e.target.closest("[data-cart-discount-apply]")) {
      e.preventDefault();
      const input = footerEl?.querySelector("[data-cart-discount-input]");
      applyDiscountCode(input?.value || "");
    }
  });

  cartOverlay?.addEventListener("submit", function (e) {
    const form = e.target.closest("[data-cart-discount-form]");
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector("[data-cart-discount-input]");
    applyDiscountCode(input?.value || "");
  });

  document.addEventListener("v4:cart-updated", refresh);

  cartBtn?.addEventListener("click", function () {
    refresh();
    openDrawer();
  });

  window.V4ShopifyCart = { openDrawer: openDrawer, refresh: refresh };

  refresh();
})();
